// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

// Trade scope pipeline — makes the procurement workflow visible.
// Every trade scope is always at exactly one stage:
//
//   Setup → Collect → Level → Award
//
// Pure functions, no React. Consumed by BidsTab scope cards and the
// package overview so the user always knows where each package stands
// and what to do next.

import type { BidScope, Bid, LevelingAdjustment } from "../projectData";
import type { BuyoutItem } from "../types";

export type PipelineStage = "setup" | "collect" | "level" | "awarded";

export const PIPELINE_STAGES: { id: PipelineStage; label: string }[] = [
  { id: "setup",   label: "Setup" },
  { id: "collect", label: "Collect" },
  { id: "level",   label: "Level" },
  { id: "awarded", label: "Award" },
];

export interface PackagePipeline {
  stage: PipelineStage;
  /** 0-based index into PIPELINE_STAGES for rendering a stepper */
  stageIndex: number;
  /** Distinct partners with an Active bid on this package */
  partnerCount: number;
  /** Active bid count */
  bidCount: number;
  /** Leveling adjustments defined for this package */
  levelingCount: number;
  /** True once a buyout row tied to this package is awarded/executed */
  awarded: boolean;
  /** Short human hint for what to do next */
  nextAction: string;
}

export function computePackagePipeline(
  scope: BidScope,
  bids: Bid[],
  levelingAdjustments: LevelingAdjustment[],
  buyoutItems: BuyoutItem[],
): PackagePipeline {
  const scopeBids = bids.filter((b) => b.scopeId === scope.id);
  const activeBids = scopeBids.filter((b) => b.status === "Active");
  const partnerCount = new Set(activeBids.map((b) => b.partnerId)).size;
  const bidCount = activeBids.length;
  const levelingCount = levelingAdjustments.filter((a) => a.scopeId === scope.id).length;

  // Awarded when a buyout row is tied to this package and marked
  // awarded/executed. Prefer the explicit bidId link; fall back to a
  // division match (legacy rows created via "Sync from Estimate").
  const scopeBidIds = new Set(scopeBids.map((b) => b.id));
  const scopeDivIds = new Set(scope.divisionIds || []);
  const awarded = buyoutItems.some(
    (bo) =>
      (bo.status === "awarded" || bo.status === "executed") &&
      ((bo.bidId && scopeBidIds.has(bo.bidId)) ||
        (!bo.bidId && bo.divisionId && scopeDivIds.has(bo.divisionId))),
  );

  let stage: PipelineStage;
  let nextAction: string;
  if (awarded) {
    stage = "awarded";
    nextAction = "Awarded — tracked in Buyout";
  } else if (partnerCount >= 2) {
    stage = "level";
    nextAction = levelingCount > 0
      ? `Level & compare ${partnerCount} bidders — then award`
      : `Compare ${partnerCount} bidders — add leveling adjustments`;
  } else if (bidCount >= 1) {
    stage = "collect";
    nextAction = "1 bidder so far — get more coverage before leveling";
  } else {
    stage = "setup";
    nextAction = "Import or enter the first bid";
  }

  return {
    stage,
    stageIndex: PIPELINE_STAGES.findIndex((s) => s.id === stage),
    partnerCount,
    bidCount,
    levelingCount,
    awarded,
    nextAction,
  };
}

/** Budget-vs-bid spread for the always-visible banner. */
export interface PlugVsBid {
  /** Estimate total for the package's divisions (the "plug" / budget) */
  budget: number;
  /** Lowest leveled bid across visible partners (0 when no bids) */
  lowBid: number;
  /** lowBid − budget: negative = under budget (good) */
  delta: number;
  hasBids: boolean;
}

export function computePlugVsBid(budget: number, leveledTotals: number[]): PlugVsBid {
  const positive = leveledTotals.filter((t) => t > 0);
  const lowBid = positive.length ? Math.min(...positive) : 0;
  return {
    budget,
    lowBid,
    delta: lowBid - budget,
    hasBids: positive.length > 0,
  };
}

// ── Auto-match bid items to estimate items ───────────────────────────────
// Conservative fuzzy match by description token overlap. Only suggests a
// match when the score clears a threshold AND is unambiguous — a wrong
// auto-alignment is worse than no alignment, because the user trusts the
// comparison grid. Drag-to-align remains the correction tool.
const STOPWORDS = new Set(["the","a","an","and","or","of","for","to","in","at","per","with","all","new"]);

function tokenize(s: string): Set<string> {
  return new Set(
    s.toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .split(/\s+/)
      .filter((t) => t.length > 1 && !STOPWORDS.has(t)),
  );
}

function jaccard(a: Set<string>, b: Set<string>): number {
  if (!a.size || !b.size) return 0;
  let inter = 0;
  for (const t of a) if (b.has(t)) inter++;
  return inter / (a.size + b.size - inter);
}

const MATCH_THRESHOLD = 0.5;   // at least half the combined tokens shared
const AMBIGUITY_GAP = 0.15;    // best must beat runner-up by this margin

/** Returns bidItemId → estimateItemId for confident matches only. */
export function suggestAlignments(
  bidItems: { id: string; description: string }[],
  estimateItems: { itemId: string; desc: string }[],
): Record<string, string> {
  const estTokens = estimateItems.map((e) => ({ itemId: e.itemId, tokens: tokenize(e.desc) }));
  const out: Record<string, string> = {};
  for (const bi of bidItems) {
    const tokens = tokenize(bi.description || "");
    if (!tokens.size) continue;
    let best: { itemId: string; score: number } | null = null;
    let second = 0;
    for (const et of estTokens) {
      const score = jaccard(tokens, et.tokens);
      if (!best || score > best.score) {
        second = best?.score ?? 0;
        best = { itemId: et.itemId, score };
      } else if (score > second) {
        second = score;
      }
    }
    if (best && best.score >= MATCH_THRESHOLD && best.score - second >= AMBIGUITY_GAP) {
      out[bi.id] = best.itemId;
    }
  }
  return out;
}

// ── Standard leveling checklist ──────────────────────────────────────────
// The items every GC levels on every package. One click inserts the ones
// not already present, so leveling starts from the industry checklist
// instead of a blank row.
export const STANDARD_LEVELING_ITEMS: string[] = [
  "General conditions / supervision",
  "Bonds",
  "Insurance",
  "Cleanup & dumpsters",
  "Hoisting / equipment",
  "Sales tax",
  "Permits & fees",
  "Escalation",
];
